import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { useEffect, useState } from "react";

interface FooterSection {
  title: string;
  links: { label: string; href: string }[];
}
interface SocialLink {
  icon: React.ElementType;
  href: string;
  label: string;
}
interface FooterItems {
  companyName: string;
  companyDescription: string;
  sections: FooterSection[];
  socialLinks: SocialLink[];
  contactInfo: {
    phone: string;
    email: string;
    address: string;
  };
  copyrightText: string;
}
interface FooterProps {
  footerItem: FooterItems;
  bgColor: string;
  textColor: string;
  linkHoverColor: string;
  className?: string

}

const Footer: React.FC<FooterProps> = ({
  bgColor,
  textColor,
  linkHoverColor,
  footerItem,
  className

}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      setIsLoggedIn(Boolean(localStorage.getItem("bt_b2b_access_token")));
    };

    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, []);

  const {
    companyName,
    companyDescription,
    sections,
    socialLinks,
    contactInfo,
    copyrightText,
  } = footerItem;
  const visibleSections = isLoggedIn
    ? sections.filter((section) => section.title.toLowerCase() !== "for business")
    : sections;

  return (
    <footer
      className={`${bgColor} ${textColor} mt-auto transition-all duration-300 ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
        {/* Responsive Column Layout for Footer */}
        <div className="grid grid-cols-1 gap-y-10 md:grid-cols-2 md:gap-x-8 lg:grid-cols-5 lg:gap-x-12">
          {/* Company Info - spans more columns on larger screens */}
          <div className="lg:col-span-2">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {companyName}
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm sm:text-base">
              {companyDescription}
            </p>
            {/* Contact Info */}
            <div className="space-y-3">
              {contactInfo.phone && (
                <div className="flex items-center gap-3">
                  <FaPhone className="text-orange-400" />
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className={`${linkHoverColor} transition-colors break-all text-sm sm:text-base`}
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              )}
              {contactInfo.email && (
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-orange-400" />
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className={`${linkHoverColor} transition-colors break-all text-sm sm:text-base`}
                  >
                    {contactInfo.email}
                  </a>
                </div>
              )}
              {contactInfo.address && (
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-orange-400 mt-1" />
                  <span className="text-gray-400 text-sm sm:text-base">
                    {contactInfo.address}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer Sections: Responsive Columns */}
          {visibleSections.map((section, idx) => (
            <div key={idx}>
              <h4 className="text-base sm:text-lg font-semibold text-white mb-4">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className={`${linkHoverColor} transition-colors duration-200 text-sm sm:text-base`}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Social + Newsletter (stacked on mobile, row on desktop) */}
        <div className="mt-10 pt-8 border-t border-gray-700">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-0 justify-between items-center">
            {/* Social Icons */}
            <div className="flex space-x-3 sm:space-x-4 justify-center items-center">
              {socialLinks.map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  aria-label={social.label}
                  className={`p-2 rounded-full bg-gray-800 ${linkHoverColor} transition-colors duration-200 hover:scale-110`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
            {/* Newsletter Signup */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className="w-full max-w-sm flex flex-col sm:flex-row gap-2"
            >
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-400 text-white placeholder-gray-400"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-200 font-medium"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div> 

      {/* Copyright and Policy Links -- stack on mobile, row on larger */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center">
          <p className="text-gray-400 text-xs sm:text-sm">
            {copyrightText}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

 {/* <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-6">
              <a
                href="/privacy"
                className={`text-xs sm:text-sm ${linkHoverColor} transition-colors`}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className={`text-xs sm:text-sm ${linkHoverColor} transition-colors`}
              >
                Terms of Service
              </a>
              <a
                href="/cookies"
                className={`text-xs sm:text-sm ${linkHoverColor} transition-colors`}
              >
                Cookie Policy
              </a>
            </div> */}
